/**
 * Non-Operating Results Section
 * Extracted from FinancePage.jsx
 */

import React, { useState } from 'react';
import { AlertTriangle, Gem, Clock, FileText, ArrowLeft, Target, CheckCircle, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Leaf, Package, RefreshCw, DollarSign, Repeat, Zap, Heart, Info, Users, GraduationCap, Home, Activity, Wallet } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip - Standard Format (Balanço Patrimonial style)
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, unit, vsBudget, vsBudgetPct, yoyChange, subtitle, language, isPP = false, tooltipData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const displayValue = (unit && (unit.includes('M') || unit === 'M')) 
    ? formatM(value) 
    : value;
  
  // For costs (negative values), favorable = actual less negative than budget (lower absolute cost)
  // For the non-cash percentage, higher is neutral/informational
  const isBudgetFavorable = vsBudgetPct !== undefined ? (value < 0 ? vsBudgetPct < 0 : vsBudgetPct >= 0) : true;
  const isYoYFavorable = yoyChange !== undefined ? (value < 0 ? yoyChange < 0 : yoyChange >= 0) : true;
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative overflow-visible ${tooltipData ? 'cursor-help' : ''}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      {/* Trend Arrow Indicator - Corner Icon */}
      <div 
        className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${
          isYoYFavorable ? 'bg-emerald-100' : 'bg-rose-100'
        }`}
      >
        {isYoYFavorable ? (
          <ArrowDownRight className="w-4 h-4 text-emerald-600" />
        ) : (
          <ArrowUpRight className="w-4 h-4 text-rose-600" />
        )}
      </div>
      
      <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{title}</p>
      <p className="font-serif text-3xl font-semibold text-primary">
        {displayValue}<span className="text-base text-slate-400 ml-1 font-sans">{unit}</span>
      </p>
      {/* Budget and YoY badges - STACKED LAYOUT (one above other) */}
      <div className="flex flex-col gap-1 mt-2">
        {vsBudgetPct !== undefined && (
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
            isPP ? 'bg-slate-100 text-slate-600' : (isBudgetFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')
          }`}>
            {vsBudgetPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {vsBudgetPct >= 0 ? '+' : ''}{vsBudgetPct}{isPP ? 'p.p.' : '%'} vs Orç.
          </span>
        )}
        {yoyChange !== undefined && (
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
            isPP ? 'bg-slate-100 text-slate-600' : (isYoYFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')
          }`}>
            {yoyChange >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {yoyChange >= 0 ? '+' : ''}{yoyChange}{isPP ? 'p.p.' : '%'} YoY
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
};

const NonOperatingResults = ({ year, language, setActiveSection, flowMap }) => {
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Key Insights - Focados na qualidade do resultado
  const nonOpInsights = {
    pt: [
      { title: 'QUALIDADE DO RESULTADO', description: 'Em 2025, os resultados não operacionais reflectem itens fora da actividade corrente, com impacto directo na leitura do resultado líquido e da qualidade dos earnings.', color: 'text-gold-400', icon: 'target' },
      { title: 'EXTRAORDINÁRIO VS ESTRUTURAL', description: 'A rubrica deve distinguir claramente efeitos pontuais de saneamento, regularizações ou write-offs, face a encargos recorrentes ou obrigações estruturais.', color: 'text-blue-400', icon: 'alert' },
      { title: 'NORMALIZAÇÃO', description: 'A leitura ajustada do resultado deve isolar os efeitos não operacionais, permitindo perceber qual teria sido o desempenho líquido sem itens extraordinários.', color: 'text-emerald-400', icon: 'check' },
    ],
    en: [
      { title: 'RESULT QUALITY', description: 'In 2025, non-operating results reflect items outside current activity, with direct impact on net income reading and earnings quality.', color: 'text-gold-400', icon: 'target' },
      { title: 'EXTRAORDINARY VS STRUCTURAL', description: 'The item should clearly distinguish one-off cleanup effects, adjustments or write-offs, from recurring charges or structural obligations.', color: 'text-blue-400', icon: 'alert' },
      { title: 'NORMALIZATION', description: 'Adjusted result reading should isolate non-operating effects, allowing understanding of what net performance would have been without extraordinary items.', color: 'text-emerald-400', icon: 'check' },
    ],
  };

  // Dados com estrutura 2024 / Orçamento 2025 / 2025
  const nonOpData = {
    // Totais principais
    total2025: -9.2,          // Real 2025
    totalBudget2025: -4.5,    // Orçamento 2025
    total2024: -2.8,          // Real 2024
    
    // Itens Extraordinários
    extraordinary2025: -6.2,
    extraordinaryBudget2025: -2.0,
    extraordinary2024: -1.0,
    
    // Itens Recorrentes/Estruturais
    recurring2025: -3.0,
    recurringBudget2025: -2.5,
    recurring2024: -1.8,
    
    // Impacto Não-Caixa
    nonCashPct2025: 48,
    nonCashPctBudget2025: 55,
    nonCashPct2024: 60,
    
    // === CAMADA A: EFEITOS EXTRAORDINÁRIOS / NÃO RECORRENTES ===
    extraordinarios: {
      // Write-off de Existências
      writeOffExistencias: {
        v2025: -4.4,    // Ajustamento extraordinário após inventário Outubro 2025
        budget2025: -0.5,
        v2024: -0.2,
      },
      
      // Correcções de Exercícios Anteriores
      correccoesAnteriores: {
        v2025: -1.5,
        budget2025: -0.8,
        v2024: -0.5,
        // Detalhe por tipo
        regularizacoesFiscais: { v2025: -0.6, budget2025: -0.3, v2024: -0.2 },
        ajustamentosImobilizado: { v2025: -0.4, budget2025: -0.2, v2024: -0.1 },
        reversoesDividas: { v2025: -0.3, budget2025: -0.2, v2024: -0.1 },
        outrasRegularizacoes: { v2025: -0.2, budget2025: -0.1, v2024: -0.1 },
      },
      
      // Outros pontuais
      outrosPontuais: {
        v2025: -0.3,
        budget2025: -0.2,
        v2024: -0.3,
      }
    },
    
    // === CAMADA B: ENCARGOS ESTRUTURAIS / PERMANENTES ===
    estruturais: {
      // Provisão Ambiental - Obrigação permanente
      provisaoAmbiental: {
        v2025: -2.0,
        budget2025: -1.8,
        v2024: -1.2,
        natureza: 'structural',
        descricao: 'Reconhecimento faseado do compromisso de recuperação ambiental da mina',
        // Dados para o gráfico de evolução (saldos no Balanço)
        saldo2024: 8.5,      // Saldo inicial no balanço
        saldo2025: 11.1,     // Saldo final no balanço
        saldoBudget2025: 10.8,
        // Componentes da variação
        provisaoRiscosEncargos: -2.0, // Componente operacional (P&L)
        unwindFinanceiro: -0.6,        // Custo financeiro do unwind
        // === DADOS PARA OS 4 KPIs BOARD-LEVEL ===
        // KPI 2: Custo unitário por m³
        custoUnitarioM3: {
          v2025: 4.2,       // USD por m³
          budget2025: 3.8,
          v2024: 3.5,
        },
        // KPI 3: Volume considerado (milhões de m³)
        volumeConsiderado: {
          v2025: 2.65,      // milhões de m³
          budget2025: 2.85,
          v2024: 2.45,
        },
        // KPI 4: Provisões para Riscos e Encargos (valor absoluto)
        provRiscosEncargos: {
          v2025: 2.0,       // M USD
          budget2025: 1.8,
          v2024: 1.2,
        },
      },
      
      // Responsabilidade Social - Programa permanente
      responsabilidadeSocial: {
        v2025: -0.8,
        budget2025: -0.6,
        v2024: -0.5,
        natureza: 'structural',
        descricao: 'Programa de investimento social nas comunidades'
      },
      
      // Outras obrigações permanentes
      outrasObrigacoes: {
        v2025: -0.2,
        budget2025: -0.1,
        v2024: -0.1,
      }
    },
    
    // Para cálculos de KPIs
    operatingResult: 28.5,
    rai: 24.3,
  };

  // Variações para KPIs
  const totalVsBudgetPct = Math.round((nonOpData.total2025 - nonOpData.totalBudget2025) / Math.abs(nonOpData.totalBudget2025) * 100);
  const totalYoYPct = Math.round((nonOpData.total2025 - nonOpData.total2024) / Math.abs(nonOpData.total2024) * 100);
  const extraordinaryVsBudgetPct = Math.round((nonOpData.extraordinary2025 - nonOpData.extraordinaryBudget2025) / Math.abs(nonOpData.extraordinaryBudget2025) * 100);
  const extraordinaryYoYPct = Math.round((nonOpData.extraordinary2024 - nonOpData.extraordinary2024) / Math.abs(nonOpData.extraordinary2024) * 100);
  const recurringVsBudgetPct = Math.round((nonOpData.recurring2025 - nonOpData.recurringBudget2025) / Math.abs(nonOpData.recurringBudget2025) * 100);
  const recurringYoYPct = Math.round((nonOpData.recurring2025 - nonOpData.recurring2024) / Math.abs(nonOpData.recurring2024) * 100);
  const nonCashVsBudget = nonOpData.nonCashPct2025 - nonOpData.nonCashPctBudget2025;
  const nonCashYoY = nonOpData.nonCashPct2025 - nonOpData.nonCashPct2024;

  return (
    <div className="space-y-6" data-testid="non-operating-results-section">
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Resultados Não Operacionais' : 'Non-Operating Results'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {nonOpInsights[language].map((insight, idx) => {
            const iconMap = { target: Target, alert: AlertTriangle, check: CheckCircle };
            const IconComponent = iconMap[insight.icon] || Target;
            const bgColors = { 'text-gold-400': 'bg-amber-500/30', 'text-blue-400': 'bg-blue-500/30', 'text-emerald-400': 'bg-emerald-500/30' };
            const iconColors = { 'text-gold-400': 'text-amber-400', 'text-blue-400': 'text-blue-400', 'text-emerald-400': 'text-emerald-400' };
            return (
              <div key={idx} className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColors[insight.color]}`}>
                    <IconComponent className={`w-5 h-5 ${iconColors[insight.color]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPIs - Original */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard 
          title={language === 'pt' ? 'Resultados Não Operacionais' : 'Non-Operating Results'} 
          value={nonOpData.total2025} 
          unit="M USD" 
          vsBudgetPct={totalVsBudgetPct}
          yoyChange={totalYoYPct}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Resultados Não Operacionais' : 'Non-Operating Results',
            definition: language === 'pt' 
              ? 'Soma de todos os custos e proveitos fora da actividade operacional corrente.'
              : 'Sum of all costs and gains outside core operating activity.',
            relevance: language === 'pt'
              ? 'Impacto directo no resultado líquido e na qualidade dos earnings reportados.'
              : 'Direct impact on net income and quality of reported earnings.',
            currentReading: language === 'pt'
              ? 'Custos não operacionais de -9.2M USD reflectem saneamento extraordinário de existências e provisões ambientais estruturais. O desvio significativo vs orçamento exige análise detalhada da natureza das rubricas.'
              : 'Non-operating costs of -9.2M USD reflect extraordinary inventory cleanup and structural environmental provisions. Significant budget deviation requires detailed analysis of item nature.'
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Itens Extraordinários' : 'Extraordinary Items'} 
          value={nonOpData.extraordinary2025} 
          unit="M USD" 
          vsBudgetPct={extraordinaryVsBudgetPct}
          yoyChange={extraordinaryYoYPct}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Itens Extraordinários' : 'Extraordinary Items',
            definition: language === 'pt' 
              ? 'Regularizações pontuais, write-offs, saneamento de existências e ajustamentos não recorrentes.'
              : 'One-off adjustments, write-offs, inventory clean-up and non-recurring items.',
            relevance: language === 'pt'
              ? 'Não devem repetir-se em exercícios futuros. Isolar para leitura normalizada.'
              : 'Should not repeat in future periods. Isolate for normalized reading.',
            currentReading: language === 'pt'
              ? 'Write-offs de -6.2M USD incluem saneamento extraordinário de existências (-4.4M) após inventário físico de Outubro 2025 e correcções de exercícios anteriores (-1.5M). Natureza one-off.'
              : 'Write-offs of -6.2M USD include extraordinary inventory cleanup (-4.4M) after October 2025 physical count and prior year corrections (-1.5M). One-off nature.'
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Itens Recorrentes' : 'Recurring Items'} 
          value={nonOpData.recurring2025} 
          unit="M USD" 
          vsBudgetPct={recurringVsBudgetPct}
          yoyChange={recurringYoYPct}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Itens Recorrentes' : 'Recurring Items',
            definition: language === 'pt' 
              ? 'Encargos estruturais previsíveis: provisões ambientais, responsabilidade social.'
              : 'Predictable structural charges: environmental provisions, social responsibility.',
            relevance: language === 'pt'
              ? 'Esperados em períodos futuros. Incorporar na previsão de resultados.'
              : 'Expected in future periods. Incorporate in results forecasting.',
            currentReading: language === 'pt'
              ? 'Custos recorrentes de -3.0M USD representam obrigações ambientais (-2.0M) e contribuições sociais à comunidade local (-0.8M). Tendência de crescimento estrutural.'
              : 'Recurring costs of -3.0M USD represent environmental obligations (-2.0M) and social contributions to local community (-0.8M). Structural growth trend.'
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Impacto Não-Caixa' : 'Non-Cash Impact'} 
          value={nonOpData.nonCashPct2025} 
          unit="%" 
          vsBudgetPct={nonCashVsBudget}
          yoyChange={nonCashYoY}
          isPP={true}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Impacto Não-Caixa' : 'Non-Cash Impact',
            definition: language === 'pt' 
              ? 'Percentagem dos resultados não operacionais sem efeito imediato na tesouraria.'
              : 'Percentage of non-operating results with no immediate treasury effect.',
            relevance: language === 'pt'
              ? 'Ajustamentos contabilísticos (provisões, imparidades) sem saída de caixa.'
              : 'Accounting adjustments (provisions, impairments) without cash outflow.',
            currentReading: language === 'pt'
              ? '48% dos custos não operacionais são ajustamentos contabilísticos sem impacto imediato em tesouraria (provisões ambientais, imparidades). O restante 52% teve impacto real em caixa (write-offs de existências).'
              : '48% of non-operating costs are accounting adjustments with no immediate treasury impact (environmental provisions, impairments). The remaining 52% had real cash impact (inventory write-offs).'
          }}
        />
      </div>

      {/* === RESUMO EXECUTIVO === */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Resultados não operacionais e extraordinários' : 'Non-operating and extraordinary results'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="non-operating-executive-summary"
      >
        <div className="space-y-6">
          {/* 1. Visão do Período */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-slate-600"></div>
              {language === 'pt' ? 'Visão do Período' : 'Period Overview'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, os <strong>resultados não operacionais líquidos</strong> fecharam em <strong>-USD 4,2M</strong>, o que compara com <strong>-USD 2,8M no Orçamento 2025</strong> e <strong>-USD 2,8M em 2024</strong>. O desvio do ano foi, portanto, de <strong>-USD 1,4M</strong> face ao plano e ao período homólogo, refletindo um agravamento de <strong>50%</strong> na componente não operacional do resultado.</>
                  : <>In 2025, <strong>net non-operating results</strong> closed at <strong>-USD 4.2M</strong>, compared to <strong>-USD 2.8M in Budget 2025</strong> and <strong>-USD 2.8M in 2024</strong>. The year's deviation was therefore <strong>-USD 1.4M</strong> versus plan and prior year, reflecting a <strong>50%</strong> worsening in the non-operating component.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura principal da página é que o agravamento não veio de uma única rubrica, mas da combinação entre <strong>maior custo extraordinário em existências</strong>, <strong>correções de exercícios anteriores mais pesadas</strong> e <strong>reforço da provisão ambiental</strong>, apenas parcialmente compensados por <strong>proveitos e ganhos não operacionais de USD 1,5M</strong>. Em termos de gestão, 2025 deve ser lido como um ano em que a componente não operacional teve impacto mais penalizador e menos favorável do que o inicialmente previsto.</>
                  : <>The main reading is that the worsening did not come from a single item, but from the combination of <strong>higher extraordinary inventory cost</strong>, <strong>heavier prior year corrections</strong> and <strong>reinforced environmental provision</strong>, only partially offset by <strong>non-operating income of USD 1.5M</strong>.</>}
              </p>
            </div>
          </div>

          {/* 2. Formação do Resultado Líquido Não Operacional */}
          <div className="bg-amber-50/60 rounded-xl p-5 border border-amber-200">
            <h4 className="font-semibold text-sm text-amber-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-amber-600"></div>
              {language === 'pt' ? 'Formação do Resultado Líquido Não Operacional' : 'Non-Operating Net Result Formation'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A estrutura de 2025 mostra <strong>proveitos não operacionais de USD 1,5M</strong> e <strong>custos/perdas não operacionais de USD 5,7M</strong>, resultando no saldo líquido de <strong>-USD 4,2M</strong>. Face ao orçamento e a 2024, o ponto central não está nos proveitos, que até melhoraram, mas sim na subida dos custos e perdas.</>
                  : <>The 2025 structure shows <strong>non-operating income of USD 1.5M</strong> and <strong>non-operating costs/losses of USD 5.7M</strong>, resulting in net balance of <strong>-USD 4.2M</strong>. Versus budget and 2024, the central point is not income, which improved, but the increase in costs and losses.</>}
              </p>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="bg-white rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase">{language === 'pt' ? 'Proveitos e Ganhos' : 'Income & Gains'}: <span className="text-emerald-800">+USD 1,5M</span></p>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    <li>• {language === 'pt' ? 'Ganhos em existências' : 'Inventory gains'}: <span className="font-medium text-emerald-700">+USD 0,8M</span></li>
                    <li>• {language === 'pt' ? 'Benefícios contratuais' : 'Contract benefits'}: <span className="font-medium text-emerald-700">+USD 0,4M</span></li>
                    <li>• {language === 'pt' ? 'Correções exerc. ant. (prov.)' : 'Prior yr. corr. (income)'}: <span className="font-medium text-emerald-700">+USD 0,3M</span></li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-3 border border-red-200">
                  <p className="text-xs font-semibold text-red-700 mb-2 uppercase">{language === 'pt' ? 'Custos e Perdas' : 'Costs & Losses'}: <span className="text-red-800">-USD 5,7M</span></p>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    <li>• {language === 'pt' ? 'Correções exerc. anteriores' : 'Prior year corrections'}: <span className="font-medium text-red-700">-USD 1,5M</span></li>
                    <li>• {language === 'pt' ? 'Prov. riscos ambientais' : 'Env. risk provisions'}: <span className="font-medium text-red-700">-USD 1,8M</span></li>
                    <li>• {language === 'pt' ? 'Prov. existências' : 'Inventory provisions'}: <span className="font-medium text-red-700">-USD 1,2M</span></li>
                    <li>• {language === 'pt' ? 'Responsabilidade social' : 'Social responsibility'}: <span className="font-medium text-red-700">-USD 0,7M</span></li>
                    <li>• {language === 'pt' ? 'Multas e penalidades' : 'Fines & penalties'}: <span className="font-medium text-red-700">-USD 0,5M</span></li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-amber-700 italic mt-2">
                {language === 'pt' 
                  ? 'Em termos executivos, o agravamento do saldo líquido foi determinado pela expansão das rubricas negativas, e não por deterioração da componente de proveitos.'
                  : 'In executive terms, the worsening of net balance was determined by expansion of negative items, not by deterioration of income component.'}
              </p>
            </div>
          </div>

          {/* 3. Itens Extraordinários e Não Recorrentes */}
          <div className="bg-rose-50/60 rounded-xl p-5 border border-rose-200">
            <h4 className="font-semibold text-sm text-rose-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-rose-600"></div>
              {language === 'pt' ? 'Itens Extraordinários e Não Recorrentes' : 'Extraordinary & Non-Recurring Items'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A componente mais material e mais diferenciadora do ano foi o <strong>ajustamento extraordinário de existências</strong>, no montante de <strong>USD 4,4M</strong>, reconhecido após inventário físico realizado em Outubro de 2025. Face a <strong>USD 0,5M no orçamento</strong> e <strong>USD 0,2M em 2024</strong>, esta foi claramente a principal rubrica extraordinária do ano.</>
                  : <>The most material and differentiating component of the year was the <strong>extraordinary inventory adjustment</strong> of <strong>USD 4.4M</strong>, recognized after physical inventory in October 2025. Versus <strong>USD 0.5M budget</strong> and <strong>USD 0.2M in 2024</strong>, this was clearly the main extraordinary item.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura de gestão aqui é dupla. Por um lado, o efeito em resultado foi material e penalizador em 2025. Por outro, trata-se de um movimento de saneamento contabilístico com natureza pontual, que reduz o risco de ajustamentos adicionais de magnitude semelhante no curto prazo.</>
                  : <>The management reading here is dual. On one hand, the P&L effect was material and penalizing in 2025. On the other, it's an accounting cleanup with one-off nature, reducing risk of similar additional adjustments in the short term.</>}
              </p>
              <div className="bg-white rounded-lg p-3 border border-rose-100 mt-2">
                <p className="text-xs text-rose-800 font-medium">
                  {language === 'pt' 
                    ? '→ Isto é relevante porque permite separar: efeitos extraordinários / não recorrentes (que penalizam 2025 mas não devem ser projectados linearmente) vs custos mais estruturais (que deverão continuar a ser monitorizados como parte da base recorrente).'
                    : '→ This is relevant as it allows separating: extraordinary / non-recurring effects (penalizing 2025 but not to be linearly projected) vs structural costs (to continue being monitored as part of recurring base).'}
                </p>
              </div>
            </div>
          </div>

          {/* 4. Itens Recorrentes e Componente Não-Caixa */}
          <div className="bg-blue-50/60 rounded-xl p-5 border border-blue-200">
            <h4 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              {language === 'pt' ? 'Itens Recorrentes e Componente Não-Caixa' : 'Recurring Items & Non-Cash Component'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Os <strong>itens recorrentes</strong> totalizaram <strong>-USD 3,0M</strong>, melhorando face ao orçamento e face a 2024, o que sugere que a deterioração do total não veio do núcleo recorrente, mas da componente extraordinária. Ao mesmo tempo, a <strong>componente não-caixa</strong> manteve peso relevante, situando-se em <strong>48%</strong> do impacto total.</>
                  : <><strong>Recurring items</strong> totaled <strong>-USD 3.0M</strong>, improving vs budget and 2024, suggesting total deterioration came not from recurring core but from extraordinary component. The <strong>non-cash component</strong> maintained relevant weight at <strong>48%</strong> of total impact.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>Este ponto é importante para o CFO porque ajuda a distinguir: impacto que penaliza directamente <strong>caixa e liquidez</strong> vs impacto que afecta sobretudo <strong>resultado contabilístico</strong> e evolução do balanço.</>
                  : <>This is important for the CFO as it helps distinguish: impact directly penalizing <strong>cash and liquidity</strong> vs impact mainly affecting <strong>accounting result</strong> and balance sheet evolution.</>}
              </p>
              <div className="bg-white rounded-lg p-3 border border-blue-100 mt-2">
                <p className="text-xs text-blue-800">
                  <span className="font-semibold">{language === 'pt' ? 'Provisão Ambiental: ' : 'Environmental Provision: '}</span>
                  {language === 'pt' 
                    ? <>Em 2025, a componente operacional foi de <strong>USD 2,0M</strong>, com o saldo da provisão a subir para <strong>USD 11,1M</strong>, suportado por custo unitário por m³ mais elevado e actualização da base de cálculo. Esta rubrica deve ser tratada como crescimento da obrigação reconhecida, e não apenas como custo do período.</>
                    : <>In 2025, operational component was <strong>USD 2.0M</strong>, with provision balance rising to <strong>USD 11.1M</strong>, supported by higher unit cost per m³ and calculation base update. This item should be treated as recognized obligation growth, not just period cost.</>}
                </p>
              </div>
            </div>
          </div>

          {/* 5. Provisão Ambiental */}
          <div className="bg-emerald-50/60 rounded-xl p-5 border border-emerald-200">
            <h4 className="font-semibold text-sm text-emerald-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
              {language === 'pt' ? 'Provisão Ambiental' : 'Environmental Provision'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A <strong>provisão para riscos e encargos ambientais</strong> foi uma das rubricas estruturais mais relevantes da secção em 2025. O impacto reconhecido no período ascendeu a <strong>USD 1,8M</strong>, acima dos <strong>USD 1,5M orçamentados</strong> e dos <strong>USD 1,2M registados em 2024</strong>, evidenciando um crescimento material da obrigação reconhecida no exercício. Em paralelo, o saldo final da provisão ambiental subiu para <strong>USD 11,1M</strong>, face a <strong>USD 8,5M em 2024</strong>, confirmando que a obrigação acumulada em balanço continuou a crescer.</>
                  : <>The <strong>environmental risks and charges provision</strong> was one of the most relevant structural items in 2025. The impact recognized in the period amounted to <strong>USD 1.8M</strong>, above the <strong>USD 1.5M budgeted</strong> and <strong>USD 1.2M recorded in 2024</strong>, evidencing material growth in the recognized obligation. The final provision balance rose to <strong>USD 11.1M</strong>, versus <strong>USD 8.5M in 2024</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura correcta deste tema não deve limitar-se ao valor contabilizado em resultados. Em 2025, a evolução da provisão foi suportada por três drivers principais:</>
                  : <>The correct reading should not be limited to the P&L value. In 2025, provision evolution was supported by three main drivers:</>}
              </p>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Custo Unitário' : 'Unit Cost'}</p>
                  <p className="text-lg font-bold text-emerald-800">USD 4,2/m³</p>
                  <p className="text-[10px] text-emerald-600">{language === 'pt' ? 'vs 3,5 em 2024' : 'vs 3.5 in 2024'}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Volume Considerado' : 'Volume Considered'}</p>
                  <p className="text-lg font-bold text-emerald-800">2,65M m³</p>
                  <p className="text-[10px] text-emerald-600">{language === 'pt' ? 'base de cálculo' : 'calculation base'}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Unwind Financeiro' : 'Financial Unwind'}</p>
                  <p className="text-lg font-bold text-emerald-800">+USD 0,6M</p>
                  <p className="text-[10px] text-emerald-600">{language === 'pt' ? 'componente financeira' : 'financial component'}</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Isto significa que a variação da provisão resulta de uma combinação entre: <strong>componente operacional</strong> (obrigação incremental gerada pela actividade do período) e <strong>componente financeira</strong> (actualização temporal da obrigação já existente).</>
                  : <>This means provision variance results from a combination of: <strong>operational component</strong> (incremental obligation from period activity) and <strong>financial component</strong> (time update of existing obligation).</>}
              </p>
              <div className="bg-white rounded-lg p-3 border border-emerald-100 mt-2">
                <p className="text-xs text-emerald-800">
                  <span className="font-semibold">{language === 'pt' ? 'Distinção crítica para a Gerência: ' : 'Critical distinction for Management: '}</span>
                  {language === 'pt' 
                    ? <>A rubrica "Provisões para Riscos e Encargos Ambientais" deve ser interpretada como o reflexo do crescimento da obrigação gerada pela operação do ano, enquanto o saldo final da provisão traduz a acumulação dessa responsabilidade no balanço. O unwind financeiro não representa novo risco ambiental nem saída operacional do período — trata-se de actualização financeira da obrigação reconhecida.</>
                    : <>The "Environmental Risks and Charges Provisions" item should be interpreted as reflecting obligation growth from year's operation, while final provision balance reflects accumulation of this liability. Financial unwind does not represent new environmental risk nor operational outflow — it's a financial update of recognized obligation.</>}
                </p>
              </div>
              <p className="text-xs text-emerald-700 mt-2">
                <span className="font-semibold">{language === 'pt' ? 'Acompanhamento em três dimensões: ' : 'Monitoring in three dimensions: '}</span>
                {language === 'pt' 
                  ? '(1) crescimento do saldo em balanço; (2) evolução do custo unitário da base de cálculo; (3) separação entre componente operacional e componente financeira. Esta leitura evita que a rubrica seja confundida com um custo extraordinário — trata-se de um encargo estrutural, directamente ligado à operação mineira e à obrigação de recuperação futura.'
                  : '(1) balance sheet balance growth; (2) unit cost evolution in calculation base; (3) separation between operational and financial component. This reading avoids confusing the item with an extraordinary cost — it is a structural charge, directly linked to mining operation and future recovery obligation.'}
              </p>
            </div>
          </div>

          {/* 6. Correções de Exercícios Anteriores */}
          <div className="bg-purple-50/60 rounded-xl p-5 border border-purple-200">
            <h4 className="font-semibold text-sm text-purple-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-purple-600"></div>
              {language === 'pt' ? 'Correções de Exercícios Anteriores' : 'Prior Year Corrections'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>As <strong>correções relativas a exercícios anteriores</strong> tiveram impacto líquido desfavorável em 2025. Do lado dos ganhos, a rubrica contribuiu com <strong>+USD 0,3M</strong>; do lado das perdas, o impacto foi de <strong>-USD 1,5M</strong>, resultando num saldo líquido de <strong>-USD 1,2M</strong>.</>
                  : <><strong>Prior year corrections</strong> had unfavorable net impact in 2025. On gains side, the item contributed <strong>+USD 0.3M</strong>; on losses side, impact was <strong>-USD 1.5M</strong>, resulting in net balance of <strong>-USD 1.2M</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>Face ao orçamento e a 2024, a deterioração veio sobretudo de: <strong>regularizações fiscais</strong>, <strong>ajustamentos de imobilizado</strong>, <strong>reversões de dívidas</strong> e <strong>outras regularizações contabilísticas</strong>.</>
                  : <>Versus budget and 2024, deterioration came mainly from: <strong>tax regularizations</strong>, <strong>fixed asset adjustments</strong>, <strong>debt reversals</strong> and <strong>other accounting regularizations</strong>.</>}
              </p>
              <p className="text-xs text-purple-700 italic">
                {language === 'pt' 
                  ? 'A leitura correcta é que se trata de uma rubrica de natureza pontual, mas cuja repetição exige disciplina reforçada de reconciliação e fecho contabilístico.'
                  : 'The correct reading is that it is a one-off nature item, but whose repetition requires reinforced reconciliation and accounting close discipline.'}
              </p>
            </div>
          </div>

          {/* 7. Responsabilidade Social */}
          <div className="bg-emerald-50/60 rounded-xl p-5 border border-emerald-200">
            <h4 className="font-semibold text-sm text-emerald-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
              {language === 'pt' ? 'Responsabilidade Social' : 'Social Responsibility'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A <strong>responsabilidade social</strong> totalizou <strong>USD 0,7M</strong> em 2025, acima dos <strong>USD 0,6M do orçamento</strong> e dos <strong>USD 0,5M de 2024</strong>. O investimento concentrou-se sobretudo em <strong>Educação e Capacitação</strong>, <strong>Água, Energia e Infra-estruturas Comunitárias</strong> e <strong>Apoio Social e Autoridades Tradicionais</strong>.</>
                  : <><strong>Social responsibility</strong> totaled <strong>USD 0.7M</strong> in 2025, above <strong>USD 0.6M budget</strong> and <strong>USD 0.5M in 2024</strong>. Investment focused mainly on <strong>Education & Training</strong>, <strong>Water, Energy & Community Infrastructure</strong> and <strong>Social Support & Traditional Authorities</strong>.</>}
              </p>
              <p className="text-xs text-emerald-700 italic">
                {language === 'pt' 
                  ? 'Esta rubrica deve ser lida como encargo recorrente com relevância institucional e ESG, e não como item extraordinário. Embora penalize o saldo não operacional, tem natureza distinta das restantes rubricas porque está associada à presença institucional da empresa e ao relacionamento com as comunidades.'
                  : 'This item should be read as recurring charge with institutional and ESG relevance, not as extraordinary. Although penalizing non-operating balance, it has distinct nature from other items as it is associated with company institutional presence and community relationships.'}
              </p>
            </div>
          </div>

          {/* 8. Síntese à Gerência */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-5 border border-[#B89A3C]/30">
            <h4 className="font-semibold text-sm text-[#8B7A2F] mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Target className="w-4 h-4" />
              {language === 'pt' ? 'Síntese à Gerência' : 'Management Summary'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, os <strong>resultados não operacionais</strong> deterioraram-se para <strong>-USD 4,2M</strong>, face a <strong>-USD 2,8M no orçamento</strong> e <strong>-USD 2,8M em 2024</strong>. O agravamento foi explicado sobretudo por três factores:</>
                  : <>In 2025, <strong>non-operating results</strong> deteriorated to <strong>-USD 4.2M</strong>, versus <strong>-USD 2.8M budget</strong> and <strong>-USD 2.8M in 2024</strong>. Worsening was mainly explained by three factors:</>}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-4">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B89A3C] mt-2 flex-shrink-0"></span>
                  <span><strong>{language === 'pt' ? 'Ajustamento extraordinário de existências' : 'Extraordinary inventory adjustment'}</strong>{language === 'pt' ? ', com impacto material em 2025' : ', with material impact in 2025'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B89A3C] mt-2 flex-shrink-0"></span>
                  <span><strong>{language === 'pt' ? 'Correções de exercícios anteriores' : 'Prior year corrections'}</strong>{language === 'pt' ? ' acima do plano' : ' above plan'}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B89A3C] mt-2 flex-shrink-0"></span>
                  <span><strong>{language === 'pt' ? 'Reforço da provisão ambiental' : 'Environmental provision reinforcement'}</strong></span>
                </li>
              </ul>
              <p>
                {language === 'pt' 
                  ? <>A componente de proveitos e ganhos melhorou, mas não foi suficiente para compensar o aumento das perdas e custos não operacionais.</>
                  : <>Income and gains component improved, but was not enough to offset the increase in non-operating losses and costs.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-[#B89A3C]/20 mt-2">
                <p className="text-sm text-[#8B7A2F]">
                  <span className="font-semibold">{language === 'pt' ? 'Foco de gestão: ' : 'Management focus: '}</span>
                  {language === 'pt' 
                    ? <>A principal distinção a manter nas páginas seguintes é: o que foi <strong>extraordinário e não recorrente</strong>; o que é <strong>estrutural e recorrente</strong>; e o que teve impacto predominantemente <strong>não-caixa</strong>. Essa separação é essencial para evitar que a leitura de 2025 distorça a avaliação da rentabilidade recorrente do negócio.</>
                    : <>The main distinction to maintain is: what was <strong>extraordinary and non-recurring</strong>; what is <strong>structural and recurring</strong>; and what had predominantly <strong>non-cash</strong> impact. This separation is essential to avoid 2025 reading distorting recurring business profitability assessment.</>}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* === GRÁFICO DE BARRAS: COMPARATIVO 2024 / ORÇ. 2025 / 2025 === */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-lg font-bold text-primary">
              {language === 'pt' ? 'Resultados Não Operacionais por Natureza' : 'Non-Operating Results by Nature'}
            </h3>
            <p className="text-slate-400 text-xs mt-0.5">
              {language === 'pt' ? '2024 vs Orçamento 2025 vs 2025 (M USD)' : '2024 vs Budget 2025 vs 2025 (M USD)'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-slate-400"></div>
              <span className="text-[10px] text-slate-500">2024</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#D4AF37]"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
              <span className="text-[10px] text-slate-500">2025</span>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart 
            data={[
              { 
                name: language === 'pt' ? 'Write-off Existências' : 'Inventory Write-off', 
                v2024: Math.abs(nonOpData.extraordinarios.writeOffExistencias.v2024), 
                budget2025: Math.abs(nonOpData.extraordinarios.writeOffExistencias.budget2025), 
                v2025: Math.abs(nonOpData.extraordinarios.writeOffExistencias.v2025),
              },
              { 
                name: language === 'pt' ? 'Corr. Exerc. Ant.' : 'Prior Yr. Corr.', 
                v2024: Math.abs(nonOpData.extraordinarios.correccoesAnteriores.v2024), 
                budget2025: Math.abs(nonOpData.extraordinarios.correccoesAnteriores.budget2025), 
                v2025: Math.abs(nonOpData.extraordinarios.correccoesAnteriores.v2025),
              },
              { 
                name: language === 'pt' ? 'Prov. Ambiental' : 'Env. Provision', 
                v2024: Math.abs(nonOpData.estruturais.provisaoAmbiental.v2024), 
                budget2025: Math.abs(nonOpData.estruturais.provisaoAmbiental.budget2025), 
                v2025: Math.abs(nonOpData.estruturais.provisaoAmbiental.v2025),
              },
              { 
                name: language === 'pt' ? 'Resp. Social' : 'Social Resp.', 
                v2024: Math.abs(nonOpData.estruturais.responsabilidadeSocial.v2024), 
                budget2025: Math.abs(nonOpData.estruturais.responsabilidadeSocial.budget2025), 
                v2025: Math.abs(nonOpData.estruturais.responsabilidadeSocial.v2025),
              },
              { 
                name: language === 'pt' ? 'Total' : 'Total', 
                v2024: Math.abs(nonOpData.total2024), 
                budget2025: Math.abs(nonOpData.totalBudget2025), 
                v2025: Math.abs(nonOpData.total2025),
              },
            ]}
            margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
            barGap={2}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 10, fill: '#64748b' }}
              interval={0}
              angle={-15}
              textAnchor="end"
              height={50}
            />
            <YAxis 
              tick={{ fontSize: 10, fill: '#64748b' }}
              tickFormatter={(v) => `${v}M`}
            />
            <Tooltip 
              formatter={(value) => [`${value.toFixed(1)}M USD`]}
              contentStyle={{ fontSize: '11px', borderRadius: '8px', backgroundColor: '#0A192F', border: 'none', color: 'white' }}
              labelStyle={{ color: '#D4AF37', fontWeight: 'bold', marginBottom: '4px' }}
            />
            <Bar dataKey="v2024" name="2024" fill="#94A3B8" radius={[3, 3, 0, 0]} />
            <Bar dataKey="budget2025" name={language === 'pt' ? 'Orç. 2025' : 'Budget 2025'} fill="#D4AF37" radius={[3, 3, 0, 0]} />
            <Bar dataKey="v2025" name="2025" fill="#0A192F" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-primary">{language === 'pt' ? 'Leitura: ' : 'Reading: '}</span>
            {language === 'pt' 
              ? <>Os resultados não operacionais de 2025 (-9.2M) foram significativamente superiores ao orçamento (-4.5M) e ao ano anterior (-2.8M). O principal driver do desvio foi o <strong>write-off extraordinário de existências</strong> (-4.4M vs 0.5M orçamentado), seguido pelas <strong>correcções de exercícios anteriores</strong> (-1.5M vs 0.8M orçamentado). Os encargos estruturais (provisão ambiental e responsabilidade social) mantiveram-se próximos do orçamentado.</>
              : <>2025 non-operating results (-9.2M) were significantly above budget (-4.5M) and prior year (-2.8M). The main deviation driver was <strong>extraordinary inventory write-off</strong> (-4.4M vs 0.5M budgeted), followed by <strong>prior year corrections</strong> (-1.5M vs 0.8M budgeted). Structural charges (environmental provision and social responsibility) remained close to budget.</>}
          </p>
        </div>
      </div>

      {/* === ARQUITECTURA EM DUAS CAMADAS === */}
      
      {/* CAMADA A: EFEITOS EXTRAORDINÁRIOS / NÃO RECORRENTES */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-amber-600 px-5 py-3">
          <h3 className="font-serif text-base font-bold text-white flex items-center gap-2">
            <Zap className="w-5 h-5" />
            {language === 'pt' ? 'Camada A — Efeitos Extraordinários / Não Recorrentes' : 'Layer A — Extraordinary / Non-Recurring Effects'}
          </h3>
          <p className="text-amber-100 text-xs mt-0.5">
            {language === 'pt' ? 'Ajustamentos pontuais registados em 2025' : 'One-off adjustments recorded in 2025'}
          </p>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Write-off de Existências 2025 - DESTAQUE PRINCIPAL */}
          <div className="bg-amber-50/80 rounded-xl p-5 border-2 border-amber-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-serif text-base font-bold text-amber-900">
                    {language === 'pt' ? 'Ajustamento Extraordinário de Existências' : 'Extraordinary Inventory Adjustment'}
                  </h4>
                  <p className="text-amber-700 text-xs">
                    {language === 'pt' ? 'Write-off após inventário físico de Outubro 2025' : 'Write-off after October 2025 physical count'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                  {language === 'pt' ? 'Extraordinário 2025' : 'Extraordinary 2025'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                  {language === 'pt' ? 'Não Recorrente' : 'Non-Recurring'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">2024</p>
                <p className="text-2xl font-bold text-slate-500">{Math.abs(nonOpData.extraordinarios.writeOffExistencias.v2024).toFixed(1)}M</p>
                <p className="text-[10px] text-slate-400">USD</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                <p className="text-[10px] text-amber-600 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</p>
                <p className="text-2xl font-bold text-amber-600">{Math.abs(nonOpData.extraordinarios.writeOffExistencias.budget2025).toFixed(1)}M</p>
                <p className="text-[10px] text-slate-400">USD</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 border border-red-200 text-center">
                <p className="text-[10px] text-red-600 uppercase tracking-wider mb-0.5">2025</p>
                <p className="text-2xl font-bold text-red-600">{Math.abs(nonOpData.extraordinarios.writeOffExistencias.v2025).toFixed(1)}M</p>
                <p className="text-[10px] text-red-500 font-medium">{language === 'pt' ? 'Extraordinário' : 'Extraordinary'}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-amber-100">
              <p className="text-sm text-slate-700 leading-relaxed">
                {language === 'pt' 
                  ? <>Em 2025 foi registado um ajustamento extraordinário de cerca de <strong>USD 4,4M</strong> em existências (peças, ferramentas e consumíveis), na sequência do inventário físico realizado em Outubro de 2025. Este movimento teve impacto pontual material, mas <strong>permitiu sanear o balanço</strong> e alinhar a contabilidade com a realidade física, reduzindo o risco de novos ajustamentos de grande magnitude.</>
                  : <>In 2025 an extraordinary adjustment of approximately <strong>USD 4.4M</strong> was recorded in inventory (parts, tools and consumables), following the physical count carried out in October 2025. This movement had material one-off impact, but <strong>allowed cleaning the balance sheet</strong> and aligning accounting with physical reality, reducing the risk of new large magnitude adjustments.</>}
              </p>
            </div>
          </div>

          {/* Correcções de Exercícios Anteriores - COM MAIS DETALHE */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-slate-500 flex items-center justify-center">
                  <RefreshCw className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-base text-slate-800">
                    {language === 'pt' ? 'Correcções de Exercícios Anteriores' : 'Prior Year Corrections'}
                  </h4>
                  <p className="text-slate-500 text-[10px]">
                    {language === 'pt' ? 'Regularizações fiscais, ajustamentos de imobilizado e reversões' : 'Tax regularizations, fixed asset adjustments and reversals'}
                  </p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wider">
                {language === 'pt' ? 'Pontual' : 'One-off'}
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-red-50 rounded-lg p-2.5 border border-red-100 text-center">
                <p className="text-[10px] text-red-600 uppercase">2025</p>
                <p className="text-lg font-bold text-red-600">{nonOpData.extraordinarios.correccoesAnteriores.v2025.toFixed(1)}M</p>
              </div>
              <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                <p className="text-[10px] text-slate-500 uppercase">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</p>
                <p className="text-lg font-bold text-slate-600">{nonOpData.extraordinarios.correccoesAnteriores.budget2025.toFixed(1)}M</p>
              </div>
              <div className="bg-white rounded-lg p-2.5 border border-slate-200 text-center">
                <p className="text-[10px] text-slate-500 uppercase">2024</p>
                <p className="text-lg font-bold text-slate-500">{nonOpData.extraordinarios.correccoesAnteriores.v2024.toFixed(1)}M</p>
              </div>
            </div>

            {/* Detalhe por tipo de correcção */}
            <div className="bg-white rounded-lg p-4 border border-slate-100 mb-3">
              <h5 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">
                {language === 'pt' ? 'Decomposição por Tipo de Correcção' : 'Breakdown by Correction Type'}
              </h5>
              <div className="space-y-2">
                {/* Cabeçalho das colunas */}
                <div className="flex items-center justify-between text-[9px] pb-2 border-b border-slate-200">
                  <span className="text-slate-500 uppercase font-semibold w-32">{language === 'pt' ? 'Tipo' : 'Type'}</span>
                  <div className="flex items-center">
                    <span className="font-bold text-slate-700 w-14 text-right">2025</span>
                    <span className="text-slate-500 w-14 text-right">{language === 'pt' ? 'Orç.' : 'Bdgt'}</span>
                    <span className="text-slate-500 w-14 text-right">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</span>
                    <span className="text-slate-500 w-12 text-right">{language === 'pt' ? 'Var. %' : 'Var. %'}</span>
                    <span className="text-slate-400 w-14 text-right">2024</span>
                    <span className="text-slate-500 w-14 text-right">{language === 'pt' ? 'Var. YoY' : 'YoY %'}</span>
                  </div>
                </div>
                {/* Regularizações Fiscais */}
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-slate-600 flex items-center gap-2 w-32">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    {language === 'pt' ? 'Regularizações Fiscais' : 'Tax Regularizations'}
                  </span>
                  <div className="flex items-center">
                    <span className="font-semibold text-red-600 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025.toFixed(1)}M</span>
                    <span className="text-slate-500 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025.toFixed(1)}M</span>
                    <span className={`w-14 text-right font-medium ${(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025).toFixed(1)}M
                    </span>
                    <span className={`w-12 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.budget2025) * 100) : 0}%
                    </span>
                    <span className="text-slate-400 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2024.toFixed(1)}M</span>
                    <span className={`w-14 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2024) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2024 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2025 - nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2024) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.regularizacoesFiscais.v2024) * 100) : 0}%
                    </span>
                  </div>
                </div>
                {/* Ajustamentos de Imobilizado */}
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-slate-600 flex items-center gap-2 w-32">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    {language === 'pt' ? 'Ajust. Imobilizado' : 'Fixed Asset Adj.'}
                  </span>
                  <div className="flex items-center">
                    <span className="font-semibold text-red-600 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025.toFixed(1)}M</span>
                    <span className="text-slate-500 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025.toFixed(1)}M</span>
                    <span className={`w-14 text-right font-medium ${(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025).toFixed(1)}M
                    </span>
                    <span className={`w-12 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.budget2025) * 100) : 0}%
                    </span>
                    <span className="text-slate-400 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2024.toFixed(1)}M</span>
                    <span className={`w-14 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2024) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2024 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2025 - nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2024) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.ajustamentosImobilizado.v2024) * 100) : 0}%
                    </span>
                  </div>
                </div>
                {/* Reversões de Dívidas */}
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-slate-600 flex items-center gap-2 w-32">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    {language === 'pt' ? 'Reversões de Dívidas' : 'Debt Reversals'}
                  </span>
                  <div className="flex items-center">
                    <span className="font-semibold text-red-600 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025.toFixed(1)}M</span>
                    <span className="text-slate-500 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025.toFixed(1)}M</span>
                    <span className={`w-14 text-right font-medium ${(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025).toFixed(1)}M
                    </span>
                    <span className={`w-12 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.budget2025) * 100) : 0}%
                    </span>
                    <span className="text-slate-400 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2024.toFixed(1)}M</span>
                    <span className={`w-14 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2024) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2024 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2025 - nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2024) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.reversoesDividas.v2024) * 100) : 0}%
                    </span>
                  </div>
                </div>
                {/* Outras Regularizações */}
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-slate-600 flex items-center gap-2 w-32">
                    <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                    {language === 'pt' ? 'Outras Regularizações' : 'Other Regularizations'}
                  </span>
                  <div className="flex items-center">
                    <span className="font-semibold text-red-600 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025.toFixed(1)}M</span>
                    <span className="text-slate-500 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025.toFixed(1)}M</span>
                    <span className={`w-14 text-right font-medium ${(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025).toFixed(1)}M
                    </span>
                    <span className={`w-12 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.budget2025) * 100) : 0}%
                    </span>
                    <span className="text-slate-400 w-14 text-right">{nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2024.toFixed(1)}M</span>
                    <span className={`w-14 text-right text-[10px] font-semibold ${(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2024) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2024 !== 0 ? Math.round((nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2025 - nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2024) / Math.abs(nonOpData.extraordinarios.correccoesAnteriores.outrasRegularizacoes.v2024) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === EVOLUÇÃO DA PROVISÃO AMBIENTAL === */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-emerald-700 px-5 py-3">
          <h3 className="font-serif text-base font-bold text-white flex items-center gap-2">
            <Leaf className="w-5 h-5" />
            {language === 'pt' ? 'Evolução da Provisão Ambiental' : 'Environmental Provision Evolution'}
          </h3>
          <p className="text-emerald-100 text-xs mt-0.5">
            {language === 'pt' ? 'Drivers económicos da provisão e ligação à obrigação reconhecida' : 'Economic drivers of provision and link to recognized obligation'}
          </p>
        </div>
        
        <div className="p-5">
          {/* === NOVA LINHA DE 4 KPIs COM DRIVERS ECONÓMICOS === */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {/* KPI 1: Saldo 2025 */}
            <div className="bg-white rounded-xl border border-emerald-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-emerald-100">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Saldo 2025' : '2025 Balance'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {nonOpData.estruturais.provisaoAmbiental.saldo2025.toFixed(1)}<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.saldo2025 - nonOpData.estruturais.provisaoAmbiental.saldoBudget2025) / nonOpData.estruturais.provisaoAmbiental.saldoBudget2025 * 100) > 0 
                    ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.saldo2025 - nonOpData.estruturais.provisaoAmbiental.saldoBudget2025) / nonOpData.estruturais.provisaoAmbiental.saldoBudget2025 * 100)}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.saldo2025 - nonOpData.estruturais.provisaoAmbiental.saldo2024) / nonOpData.estruturais.provisaoAmbiental.saldo2024 * 100) > 0 
                    ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.saldo2025 - nonOpData.estruturais.provisaoAmbiental.saldo2024) / nonOpData.estruturais.provisaoAmbiental.saldo2024 * 100)}% YoY
                </span>
              </div>
            </div>

            {/* KPI 2: Custo Unitário por m³ */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-amber-100">
                <TrendingUp className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Custo unitário por m³' : 'Unit cost per m³'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2025.toFixed(1)}<span className="text-base text-slate-400 ml-1 font-sans">USD/m³</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2025 - nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.budget2025) / nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.budget2025 * 100) > 0 
                    ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2025 - nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.budget2025) / nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.budget2025 * 100)}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2025 - nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2024) / nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2024 * 100) > 0 
                    ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2025 - nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2024) / nonOpData.estruturais.provisaoAmbiental.custoUnitarioM3.v2024 * 100)}% YoY
                </span>
              </div>
            </div>

            {/* KPI 3: Volume Considerado */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-emerald-100">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Volume considerado' : 'Volume considered'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2025.toFixed(2)}<span className="text-base text-slate-400 ml-1 font-sans">M m³</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2025 - nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.budget2025) / nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.budget2025 * 100) < 0 
                    ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}>
                  <TrendingDown className="w-3 h-3" />
                  {Math.round((nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2025 - nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.budget2025) / nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.budget2025 * 100)}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2025 - nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2024) / nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2024 * 100) > 0 
                    ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2025 - nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2024) / nonOpData.estruturais.provisaoAmbiental.volumeConsiderado.v2024 * 100)}% YoY
                </span>
              </div>
            </div>

            {/* KPI 4: Provisões para Riscos e Encargos */}
            <div className="bg-white rounded-xl border border-emerald-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-emerald-100">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Prov. Riscos e Encargos' : 'Risks & Charges Prov.'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2025.toFixed(1)}<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2025 - nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.budget2025) / nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.budget2025 * 100) > 0 
                    ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2025 - nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.budget2025) / nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.budget2025 * 100)}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  ((nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2025 - nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2024) / nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2024 * 100) > 0 
                    ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  +{Math.round((nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2025 - nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2024) / nonOpData.estruturais.provisaoAmbiental.provRiscosEncargos.v2024 * 100)}% YoY
                </span>
              </div>
            </div>
          </div>

          {/* Gráfico Bridge/Cascata - MANTIDO */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-700">
                {language === 'pt' ? 'Decomposição da Variação do Saldo' : 'Balance Variance Decomposition'}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-[#0A192F]"></div>
                  <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Saldos' : 'Balances'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-emerald-600"></div>
                  <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Provisões (Operacional)' : 'Provisions (Operational)'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-slate-400"></div>
                  <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Unwind (Financeiro)' : 'Unwind (Financial)'}</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart 
                data={[
                  { name: language === 'pt' ? 'Saldo 2024' : '2024 Balance', value: nonOpData.estruturais.provisaoAmbiental.saldo2024, type: 'balance' },
                  { name: language === 'pt' ? 'Prov. Riscos e Encargos' : 'Risks & Charges Prov.', value: Math.abs(nonOpData.estruturais.provisaoAmbiental.provisaoRiscosEncargos), type: 'operational' },
                  { name: language === 'pt' ? 'Unwind Financeiro' : 'Financial Unwind', value: Math.abs(nonOpData.estruturais.provisaoAmbiental.unwindFinanceiro), type: 'financial' },
                  { name: language === 'pt' ? 'Saldo 2025' : '2025 Balance', value: nonOpData.estruturais.provisaoAmbiental.saldo2025, type: 'balance' },
                ]}
                margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval={0}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  domain={[0, 14]}
                  tickFormatter={(v) => `${v}M`}
                />
                <Tooltip 
                  formatter={(value, name, props) => {
                    const type = props.payload.type;
                    if (type === 'balance') return [`${value.toFixed(1)}M USD`, language === 'pt' ? 'Saldo' : 'Balance'];
                    if (type === 'operational') return [`+${value.toFixed(1)}M USD`, language === 'pt' ? 'Componente Operacional' : 'Operational Component'];
                    return [`+${value.toFixed(1)}M USD`, language === 'pt' ? 'Componente Financeiro' : 'Financial Component'];
                  }}
                  contentStyle={{ fontSize: '11px', borderRadius: '8px', backgroundColor: '#0A192F', border: 'none', color: 'white' }}
                  labelStyle={{ color: '#D4AF37', fontWeight: 'bold', marginBottom: '4px' }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {[
                    { type: 'balance' },
                    { type: 'operational' },
                    { type: 'financial' },
                    { type: 'balance' },
                  ].map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={
                        entry.type === 'balance' ? '#0A192F' :
                        entry.type === 'operational' ? '#059669' : '#94a3b8'
                      }
                      strokeWidth={entry.type === 'operational' ? 3 : 0}
                      stroke={entry.type === 'operational' ? '#047857' : 'none'}
                    />
                  ))}
                  <LabelList 
                    dataKey="value" 
                    position="top" 
                    formatter={(v) => `${v.toFixed(1)}M`}
                    style={{ fontSize: '11px', fontWeight: '600', fill: '#1e293b' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* === CAIXA DE LEITURA ESTRATÉGICA BOARD-LEVEL === */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-5 border border-emerald-200">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-serif text-base font-bold text-emerald-900 mb-3">
                  {language === 'pt' ? 'Leitura Estratégica da Componente Operacional' : 'Strategic Reading of Operational Component'}
                </h4>
                
                <div className="space-y-3">
                  {/* Parágrafo 1: Enquadramento geral */}
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {language === 'pt' 
                      ? <>O aumento do <strong>saldo da provisão ambiental</strong> em 2025 deve ser lido sobretudo à luz da componente operacional do período, suportada pela combinação entre <strong>volume considerado</strong> e <strong>custo unitário por m³</strong> aplicado na base de cálculo.</>
                      : <>The increase in the <strong>environmental provision balance</strong> in 2025 should be read primarily in light of the operational component of the period, supported by the combination of <strong>volume considered</strong> and <strong>unit cost per m³</strong> applied in the calculation base.</>}
                  </p>
                  
                  {/* Parágrafo 2: Drivers operacionais */}
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {language === 'pt' 
                      ? <>A rubrica <strong>Provisões para Riscos e Encargos Ambientais</strong> traduz o reconhecimento da obrigação incremental gerada pela operação do ano, enquanto o <strong>saldo final</strong> reflecte a acumulação dessa obrigação no balanço.</>
                      : <>The <strong>Environmental Risks and Charges Provisions</strong> item reflects the recognition of incremental obligation generated by the year's operation, while the <strong>final balance</strong> reflects the accumulation of that obligation in the balance sheet.</>}
                  </p>
                  
                  {/* Parágrafo 3: Implicação Board */}
                  <div className="bg-white/60 rounded-lg p-3 border border-emerald-200/50">
                    <p className="text-sm text-emerald-800 leading-relaxed">
                      <span className="font-semibold">{language === 'pt' ? 'Implicação para o Board: ' : 'Board Implication: '}</span>
                      {language === 'pt' 
                        ? <>Esta leitura é relevante porque permite distinguir entre crescimento da obrigação por <strong>efeito operacional real</strong> e evolução meramente financeira da provisão.</>
                        : <>This reading is relevant because it allows distinguishing between obligation growth due to <strong>real operational effect</strong> and merely financial evolution of the provision.</>}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-200 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                    {language === 'pt' ? 'Estrutural' : 'Structural'}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
                    {language === 'pt' ? 'Unwind em Res. Financeiros' : 'Unwind in Fin. Results'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === RESPONSABILIDADE SOCIAL - SECÇÃO BOARD-READY === */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Nível 1: Cabeçalho premium */}
        <div className="bg-gradient-to-r from-rose-700 to-rose-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-bold text-white">
                  {language === 'pt' ? 'Responsabilidade Social' : 'Social Responsibility'}
                </h3>
                <p className="text-rose-100 text-xs mt-0.5">
                  {language === 'pt' ? 'Programa de investimento social nas comunidades' : 'Community social investment program'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider">
                {language === 'pt' ? 'Programa Permanente' : 'Permanent Program'}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-rose-100 text-[10px] font-medium">
                {language === 'pt' ? 'Encargo recorrente com impacto social e relevância ESG' : 'Recurring charge with social impact and ESG relevance'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* Nível 2: KPIs financeiros principais */}
          <div className="mb-6">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-3 font-medium">
              {language === 'pt' ? 'Leitura financeira da rubrica em Resultados Não Operacionais' : 'Financial reading of item in Non-Operating Results'}
            </p>
            <div className="grid grid-cols-5 gap-3">
              <div className="bg-rose-50 rounded-lg p-3 border border-rose-100 text-center">
                <p className="text-[10px] text-rose-600 uppercase tracking-wider mb-0.5">2025</p>
                <p className="text-2xl font-bold text-rose-700">{Math.abs(nonOpData.estruturais.responsabilidadeSocial.v2025).toFixed(1)}M</p>
                <p className="text-[9px] text-rose-500">USD</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</p>
                <p className="text-2xl font-bold text-slate-500">{Math.abs(nonOpData.estruturais.responsabilidadeSocial.budget2025).toFixed(1)}M</p>
                <p className="text-[9px] text-slate-400">USD</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">2024</p>
                <p className="text-2xl font-bold text-slate-500">{Math.abs(nonOpData.estruturais.responsabilidadeSocial.v2024).toFixed(1)}M</p>
                <p className="text-[9px] text-slate-400">USD</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100 text-center">
                <p className="text-[10px] text-emerald-600 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Var. YoY' : 'Var. YoY'}</p>
                <p className="text-2xl font-bold text-emerald-700">+60%</p>
                <p className="text-[9px] text-emerald-500">+0.3M USD</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100 text-center">
                <p className="text-[10px] text-amber-600 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Var. vs Orç.' : 'Var. vs Bdgt'}</p>
                <p className="text-2xl font-bold text-amber-700">+33%</p>
                <p className="text-[9px] text-amber-500">+0.2M USD</p>
              </div>
            </div>
          </div>

          {/* Nível 3: Gráfico de investimento por eixo de intervenção */}
          <div className="bg-slate-50/50 rounded-xl p-5 border border-slate-100 mb-6">
            <div className="mb-4">
              <h4 className="font-serif text-base font-semibold text-primary">
                {language === 'pt' ? 'Investimento por Eixo de Intervenção | 2025 vs 2024' : 'Investment by Intervention Axis | 2025 vs 2024'}
              </h4>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'pt' ? 'Distribuição por área de intervenção (M USD)' : 'Distribution by intervention area (M USD)'}
              </p>
            </div>
            
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={[
                  { name: language === 'pt' ? 'Educação e Capacitação' : 'Education & Training', v2025: 0.28, v2024: 0.18 },
                  { name: language === 'pt' ? 'Água, Energia e Infra.' : 'Water, Energy & Infra.', v2025: 0.22, v2024: 0.12 },
                  { name: language === 'pt' ? 'Apoio Social e Sobas' : 'Social Support & Chiefs', v2025: 0.15, v2024: 0.10 },
                  { name: language === 'pt' ? 'Desporto e Cultura' : 'Sports & Culture', v2025: 0.08, v2024: 0.05 },
                  { name: language === 'pt' ? 'Desenv. Económico Local' : 'Local Econ. Dev.', v2025: 0.07, v2024: 0.05 },
                ]}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                barCategoryGap="25%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                <XAxis 
                  type="number" 
                  tick={{ fontSize: 10, fill: '#64748B' }}
                  tickFormatter={(value) => `${value.toFixed(2)}M`}
                  domain={[0, 0.35]}
                />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fontSize: 9, fill: '#334155', fontWeight: 500 }}
                  width={110}
                />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(2)}M USD`]}
                  contentStyle={{ fontSize: '11px', borderRadius: '8px', backgroundColor: '#0A192F', border: 'none', color: 'white' }}
                  labelStyle={{ color: '#D4AF37', fontWeight: 'bold', marginBottom: '4px' }}
                />
                <Bar dataKey="v2025" fill="#be123c" name="2025" radius={[0, 4, 4, 0]} barSize={10} />
                <Bar dataKey="v2024" fill="#94A3B8" name="2024" radius={[0, 4, 4, 0]} barSize={10} />
              </BarChart>
            </ResponsiveContainer>
            
            {/* Legend */}
            <div className="flex justify-center gap-6 mt-3 pt-3 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#be123c' }}></div>
                <span className="text-xs text-slate-500">2025</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#94A3B8' }}></div>
                <span className="text-xs text-slate-500">2024</span>
              </div>
            </div>
          </div>

          {/* Nível 3b: Principais Acções Executadas por Eixo - Tabela Executiva */}
          <div className="bg-slate-50/50 rounded-xl p-5 border border-slate-100 mb-6">
            <div className="mb-4">
              <h4 className="font-serif text-base font-semibold text-primary">
                {language === 'pt' ? 'Principais Acções Executadas por Eixo' : 'Main Actions Executed by Axis'}
              </h4>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'pt' ? 'Acções concretas e objectivos estratégicos de cada eixo de intervenção' : 'Concrete actions and strategic objectives of each intervention axis'}
              </p>
            </div>
            
            {/* Tabela executiva */}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              {/* Cabeçalho */}
              <div className="grid grid-cols-12 bg-slate-100 text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                <div className="col-span-3 px-3 py-2 border-r border-slate-200">{language === 'pt' ? 'Eixo de Intervenção' : 'Intervention Axis'}</div>
                <div className="col-span-5 px-3 py-2 border-r border-slate-200">{language === 'pt' ? 'Principais Acções Executadas' : 'Main Actions Executed'}</div>
                <div className="col-span-4 px-3 py-2">{language === 'pt' ? 'Leitura Executiva / Objectivo' : 'Executive Reading / Objective'}</div>
              </div>
              
              {/* Linha 1: Educação e Capacitação */}
              <div className="grid grid-cols-12 text-xs border-t border-slate-200 hover:bg-slate-50/80 transition-colors">
                <div className="col-span-3 px-3 py-3 border-r border-slate-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#C5A05920' }}>
                    <GraduationCap className="w-3.5 h-3.5" style={{ color: '#C5A059' }} />
                  </div>
                  <span className="font-medium text-slate-700">{language === 'pt' ? 'Educação e Capacitação' : 'Education & Training'}</span>
                </div>
                <div className="col-span-5 px-3 py-3 border-r border-slate-100 text-slate-600">
                  <ul className="space-y-0.5">
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Construção e reparação de escolas' : 'School construction and repair'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Bolsas de estudo e apoio à cultura' : 'Scholarships and cultural support'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Merendas escolares (700 alunos)' : 'School meals (700 students)'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Oferta de 700 kits escolares' : '700 school kits donated'}</li>
                  </ul>
                </div>
                <div className="col-span-4 px-3 py-3 text-slate-500 italic text-[11px]">
                  {language === 'pt' ? 'Reforço do acesso à educação e apoio directo a alunos das comunidades' : 'Strengthening access to education and direct support to community students'}
                </div>
              </div>
              
              {/* Linha 2: Água, Energia e Infra-estruturas */}
              <div className="grid grid-cols-12 text-xs border-t border-slate-200 hover:bg-slate-50/80 transition-colors">
                <div className="col-span-3 px-3 py-3 border-r border-slate-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#3B82F620' }}>
                    <Zap className="w-3.5 h-3.5" style={{ color: '#3B82F6' }} />
                  </div>
                  <span className="font-medium text-slate-700">{language === 'pt' ? 'Água, Energia e Infra.' : 'Water, Energy & Infra.'}</span>
                </div>
                <div className="col-span-5 px-3 py-3 border-r border-slate-100 text-slate-600">
                  <ul className="space-y-0.5">
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Furos de água nas comunidades' : 'Water wells in communities'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Iluminação pública com painéis solares (100 casas)' : 'Public lighting with solar panels (100 homes)'}</li>
                  </ul>
                </div>
                <div className="col-span-4 px-3 py-3 text-slate-500 italic text-[11px]">
                  {language === 'pt' ? 'Melhoria das condições básicas de vida e acesso a recursos essenciais' : 'Improving basic living conditions and access to essential resources'}
                </div>
              </div>
              
              {/* Linha 3: Apoio Social e Autoridades Tradicionais */}
              <div className="grid grid-cols-12 text-xs border-t border-slate-200 hover:bg-slate-50/80 transition-colors">
                <div className="col-span-3 px-3 py-3 border-r border-slate-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#8B5CF620' }}>
                    <Users className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                  </div>
                  <span className="font-medium text-slate-700">{language === 'pt' ? 'Apoio Social e Sobas' : 'Social Support & Chiefs'}</span>
                </div>
                <div className="col-span-5 px-3 py-3 border-r border-slate-100 text-slate-600">
                  <ul className="space-y-0.5">
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Donativos financeiros às comunidades' : 'Financial donations to communities'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Cesta básica mensal aos sobas' : 'Monthly basic food basket to chiefs'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Mensalidade dos sobas' : 'Monthly allowance for chiefs'}</li>
                  </ul>
                </div>
                <div className="col-span-4 px-3 py-3 text-slate-500 italic text-[11px]">
                  {language === 'pt' ? 'Apoio social directo e reforço institucional junto das autoridades tradicionais' : 'Direct social support and institutional strengthening with traditional authorities'}
                </div>
              </div>
              
              {/* Linha 4: Desporto, Cultura e Coesão Social */}
              <div className="grid grid-cols-12 text-xs border-t border-slate-200 hover:bg-slate-50/80 transition-colors">
                <div className="col-span-3 px-3 py-3 border-r border-slate-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#F5920620' }}>
                    <Activity className="w-3.5 h-3.5" style={{ color: '#F59206' }} />
                  </div>
                  <span className="font-medium text-slate-700">{language === 'pt' ? 'Desporto e Cultura' : 'Sports & Culture'}</span>
                </div>
                <div className="col-span-5 px-3 py-3 border-r border-slate-100 text-slate-600">
                  <ul className="space-y-0.5">
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Conclusão de recintos desportivos' : 'Completion of sports facilities'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Apoio a eventos culturais locais' : 'Support for local cultural events'}</li>
                  </ul>
                </div>
                <div className="col-span-4 px-3 py-3 text-slate-500 italic text-[11px]">
                  {language === 'pt' ? 'Coesão social e dinamização comunitária através do desporto e cultura' : 'Social cohesion and community dynamization through sports and culture'}
                </div>
              </div>
              
              {/* Linha 5: Desenvolvimento Económico Local */}
              <div className="grid grid-cols-12 text-xs border-t border-slate-200 hover:bg-slate-50/80 transition-colors">
                <div className="col-span-3 px-3 py-3 border-r border-slate-100 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#05966920' }}>
                    <Wallet className="w-3.5 h-3.5" style={{ color: '#059669' }} />
                  </div>
                  <span className="font-medium text-slate-700">{language === 'pt' ? 'Desenv. Económico Local' : 'Local Econ. Dev.'}</span>
                </div>
                <div className="col-span-5 px-3 py-3 border-r border-slate-100 text-slate-600">
                  <ul className="space-y-0.5">
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Lavras comunitárias' : 'Community farming'}</li>
                    <li className="flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>{language === 'pt' ? 'Aquisição de serviços de fornecedores locais' : 'Procurement of local supplier services'}</li>
                  </ul>
                </div>
                <div className="col-span-4 px-3 py-3 text-slate-500 italic text-[11px]">
                  {language === 'pt' ? 'Promoção de rendimento e dinamização da economia local' : 'Income promotion and local economy dynamization'}
                </div>
              </div>
            </div>
          </div>

          {/* Nível 4: Leitura executiva para o board */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-5 border border-[#B89A3C]/30">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#B89A3C] flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-[#8B7A2F] mb-2 uppercase tracking-wider">
                  {language === 'pt' ? 'Leitura Executiva para o Board' : 'Executive Reading for the Board'}
                </h4>
                <div className="space-y-3">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {language === 'pt' 
                      ? <>A responsabilidade social totalizou <strong>USD 0,8M em 2025</strong>, acima de 2024 (<strong>+60%</strong>) e do orçamento (<strong>+33%</strong>), refletindo o reforço do programa de investimento social nas comunidades envolventes à operação mineira.</>
                      : <>Social responsibility totaled <strong>USD 0.8M in 2025</strong>, above 2024 (<strong>+60%</strong>) and budget (<strong>+33%</strong>), reflecting the reinforcement of the community social investment program surrounding the mining operation.</>}
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {language === 'pt' 
                      ? <>O investimento concentrou-se sobretudo nos eixos de <strong>Educação e Capacitação</strong> (35%) e <strong>Água, Energia e Infra-estruturas Comunitárias</strong> (28%), seguidos de <strong>Apoio Social e Autoridades Tradicionais</strong> (19%). As acções concretas incluíram construção de escolas, distribuição de kits e merendas escolares, abertura de furos de água, iluminação solar, cestas básicas e mensalidades aos sobas, recintos desportivos e lavras comunitárias.</>
                      : <>Investment focused primarily on <strong>Education and Training</strong> (35%) and <strong>Water, Energy and Community Infrastructure</strong> (28%), followed by <strong>Social Support and Traditional Authorities</strong> (19%). Concrete actions included school construction, distribution of school kits and meals, water well drilling, solar lighting, basic food baskets and allowances to chiefs, sports facilities and community farming.</>}
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed bg-white/50 rounded-lg p-3 border border-[#B89A3C]/20">
                    <span className="font-semibold text-[#8B7A2F]">{language === 'pt' ? 'Relevância Institucional e ESG: ' : 'Institutional and ESG Relevance: '}</span>
                    {language === 'pt' 
                      ? 'A rubrica deve ser lida como encargo recorrente com forte relevância institucional e alinhamento com compromissos ESG, permanecendo como componente estrutural dos resultados não operacionais.'
                      : 'The item should be read as a recurring charge with strong institutional relevance and ESG alignment, remaining as a structural component of non-operating results.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === LEITURA EXECUTIVA TRANSVERSAL - 3 MINI-CARDS === */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-serif text-lg font-bold text-primary mb-4">
          {language === 'pt' ? 'Leitura Executiva Transversal' : 'Cross-Sectional Executive Reading'}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {/* Card 1: Extraordinário vs Estrutural */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 border border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-amber-600 flex items-center justify-center">
                <Zap className="w-3.5 h-3.5 text-white" />
              </div>
              <h4 className="font-semibold text-sm text-amber-900">{language === 'pt' ? 'Extraordinário vs Estrutural' : 'Extraordinary vs Structural'}</h4>
            </div>
            <div className="space-y-2 mb-3">
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">2024 {language === 'pt' ? 'Extraord.' : 'Extraord.'}</span>
                  <span className="font-bold text-slate-500">36%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-slate-400 h-2 rounded-full" style={{ width: '36%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">{language === 'pt' ? 'Orç. 2025 Extraord.' : 'Bdgt 2025 Extraord.'}</span>
                  <span className="font-bold text-[#B89A3C]">44%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-[#D4AF37] h-2 rounded-full" style={{ width: '44%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">2025 {language === 'pt' ? 'Extraord.' : 'Extraord.'}</span>
                  <span className="font-bold text-amber-700">67%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-amber-500 h-2 rounded-full" style={{ width: '67%' }}></div>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-amber-800 leading-relaxed">
              {language === 'pt' 
                ? 'Em 2025 os itens extraordinários representaram 67% do total, muito acima do orçamentado (44%) e de 2024 (36%). Write-off de existências foi o principal responsável.'
                : 'In 2025 extraordinary items represented 67% of total, well above budget (44%) and 2024 (36%). Inventory write-off was the main driver.'}
            </p>
          </div>

          {/* Card 2: Caixa vs Não-Caixa */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                <DollarSign className="w-3.5 h-3.5 text-white" />
              </div>
              <h4 className="font-semibold text-sm text-blue-900">{language === 'pt' ? 'Caixa vs Não-Caixa' : 'Cash vs Non-Cash'}</h4>
            </div>
            <div className="space-y-2 mb-3">
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">2024 {language === 'pt' ? 'Não-Caixa' : 'Non-Cash'}</span>
                  <span className="font-bold text-slate-500">{nonOpData.nonCashPct2024}%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-slate-400 h-2 rounded-full" style={{ width: `${nonOpData.nonCashPct2024}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">{language === 'pt' ? 'Orç. 2025 Não-Caixa' : 'Bdgt 2025 Non-Cash'}</span>
                  <span className="font-bold text-[#B89A3C]">{nonOpData.nonCashPctBudget2025}%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-[#D4AF37] h-2 rounded-full" style={{ width: `${nonOpData.nonCashPctBudget2025}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className="text-slate-600">2025 {language === 'pt' ? 'Não-Caixa' : 'Non-Cash'}</span>
                  <span className="font-bold text-blue-700">{nonOpData.nonCashPct2025}%</span>
                </div>
                <div className="w-full bg-white rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${nonOpData.nonCashPct2025}%` }}></div>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-blue-800 leading-relaxed">
              {language === 'pt' 
                ? 'Em 2025, apenas 48% dos custos são não-caixa, abaixo do orçamentado (55%). O write-off de existências teve impacto real em caixa, diferente das provisões.'
                : 'In 2025, only 48% of costs are non-cash, below budget (55%). Inventory write-off had real cash impact, unlike provisions.'}
            </p>
          </div>

          {/* Card 3: Síntese Gerência */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-4 border border-[#B89A3C]/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#B89A3C] flex items-center justify-center">
                <Target className="w-3.5 h-3.5 text-white" />
              </div>
              <h4 className="font-semibold text-sm text-[#8B7A2F]">{language === 'pt' ? 'Síntese à Gerência' : 'Management Summary'}</h4>
            </div>
            <div className="space-y-1.5 mb-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">2024</span>
                <span className="font-bold text-slate-500">-2.8M</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</span>
                <span className="font-bold text-[#B89A3C]">-4.5M</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">2025</span>
                <span className="font-bold text-red-600">-9.2M</span>
              </div>
              <div className="border-t border-[#B89A3C]/30 pt-1.5 flex justify-between items-center">
                <span className="font-semibold text-red-700">{language === 'pt' ? 'Desvio vs Orç.' : 'Var. vs Bdgt'}</span>
                <span className="font-bold text-red-600">+104%</span>
              </div>
            </div>
            <p className="text-[10px] text-[#6B5A1F] leading-relaxed">
              {language === 'pt' 
                ? 'Desvio de 104% vs orçamento explicado maioritariamente pelo write-off extraordinário de existências (-4.4M vs -0.5M orç.).'
                : '104% budget deviation explained mainly by extraordinary inventory write-off (-4.4M vs -0.5M bdgt).'}
            </p>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-[#0A192F] px-5 py-3">
          <h3 className="font-serif text-lg font-bold text-white">
            {language === 'pt' ? 'Resultados Não Operacionais' : 'Non-Operating Results'}
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0A192F] text-white">
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider">{language === 'pt' ? 'Descrição' : 'Description'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider">2025</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. %' : 'Var. %'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">2024</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}</th>
            </tr>
          </thead>
          <tbody>
            {/* Income Section */}
            <tr className="bg-emerald-50 border-b border-emerald-100">
              <td colSpan="7" className="py-2.5 px-4 font-semibold text-sm text-emerald-800">
                {language === 'pt' ? 'Outros proveitos e ganhos não operacionais' : 'Other non-operating income and gains'}
              </td>
            </tr>
            {[
              { name: { pt: 'Ganhos em existências', en: 'Inventory gains' }, v2025: 0.8, budget: 0.5, v2024: 0.5 },
              { name: { pt: 'Benefícios penalidades contratuais', en: 'Contract penalty benefits' }, v2025: 0.4, budget: 0.2, v2024: 0.2 },
              { name: { pt: 'Correcções relat. exerc. anteriores', en: 'Prior year corrections' }, v2025: 0.3, budget: 0.1, v2024: 0.1 },
            ].map((item, idx) => {
              const varAbs = item.v2025 - item.budget;
              const varPct = item.budget !== 0 ? ((item.v2025 - item.budget) / Math.abs(item.budget) * 100).toFixed(0) : 0;
              const varYoY = item.v2024 !== 0 ? ((item.v2025 - item.v2024) / Math.abs(item.v2024) * 100).toFixed(0) : 0;
              return (
              <tr key={`gain-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-4 pl-8 text-slate-700">{item.name[language]}</td>
                <td className="py-2 px-3 text-right font-medium text-emerald-600 tabular-nums">{item.v2025.toFixed(1)}</td>
                <td className="py-2 px-3 text-right text-slate-500 tabular-nums">{item.budget.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varPct}%</td>
                <td className="py-2 px-3 text-right text-slate-400 tabular-nums">{item.v2024.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${parseFloat(varYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%</td>
              </tr>
            )})}
            <tr className="bg-emerald-50/50 border-b border-emerald-200">
              <td className="py-2 px-4 font-semibold text-emerald-800">{language === 'pt' ? 'Subtotal Proveitos' : 'Income Subtotal'}</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-700 tabular-nums">1.5</td>
              <td className="py-2 px-3 text-right font-medium text-slate-600 tabular-nums">0.8</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-600 tabular-nums">+0.7</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-600 tabular-nums">+88%</td>
              <td className="py-2 px-3 text-right font-medium text-slate-400 tabular-nums">0.8</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-600 tabular-nums">+88%</td>
            </tr>

            {/* Costs Section */}
            <tr className="bg-red-50 border-b border-red-100">
              <td colSpan="7" className="py-2.5 px-4 font-semibold text-sm text-red-800">
                {language === 'pt' ? 'Outros custos e perdas não operacionais' : 'Other non-operating costs and losses'}
              </td>
            </tr>
            {[
              { name: { pt: 'Provisões do exercício para Existências', en: 'Inventory provisions' }, v2025: -1.2, budget: -0.8, v2024: -0.8 },
              { name: { pt: 'Provisões para Riscos e encargos ambientais', en: 'Environmental risk provisions' }, v2025: -1.8, budget: -1.5, v2024: -1.2 },
              { name: { pt: 'Multas e penalidades contratuais', en: 'Fines and contract penalties' }, v2025: -0.5, budget: -0.2, v2024: -0.3 },
              { name: { pt: 'Correcções relat. exerc. anteriores', en: 'Prior year corrections' }, v2025: -1.5, budget: -0.5, v2024: -0.8 },
              { name: { pt: 'Responsabilidade Social', en: 'Social Responsibility' }, v2025: -0.7, budget: -0.6, v2024: -0.5 },
            ].map((item, idx) => {
              const varAbs = item.v2025 - item.budget;
              const varPct = item.budget !== 0 ? ((item.v2025 - item.budget) / Math.abs(item.budget) * 100).toFixed(0) : 0;
              const varYoY = item.v2024 !== 0 ? ((item.v2025 - item.v2024) / Math.abs(item.v2024) * 100).toFixed(0) : 0;
              return (
              <tr key={`cost-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-4 pl-8 text-slate-700">{item.name[language]}</td>
                <td className="py-2 px-3 text-right font-medium text-red-600 tabular-nums">{item.v2025.toFixed(1)}</td>
                <td className="py-2 px-3 text-right text-slate-500 tabular-nums">{item.budget.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varPct}%</td>
                <td className="py-2 px-3 text-right text-slate-400 tabular-nums">{item.v2024.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${parseFloat(varYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%</td>
              </tr>
            )})}
            <tr className="bg-red-50/50 border-b border-red-200">
              <td className="py-2 px-4 font-semibold text-red-800">{language === 'pt' ? 'Subtotal Custos' : 'Costs Subtotal'}</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">-5.7</td>
              <td className="py-2 px-3 text-right font-medium text-slate-600 tabular-nums">-3.6</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">-2.1</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">+58%</td>
              <td className="py-2 px-3 text-right font-medium text-slate-400 tabular-nums">-3.6</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">+58%</td>
            </tr>

            {/* Total */}
            <tr className="bg-gold-50/50">
              <td className="py-3 px-4 font-bold text-primary">{language === 'pt' ? 'TOTAL RESULTADOS NÃO OPERACIONAIS' : 'TOTAL NON-OPERATING RESULTS'}</td>
              <td className="py-3 px-3 text-right text-base font-bold text-primary tabular-nums">-4.2</td>
              <td className="py-3 px-3 text-right font-semibold text-slate-600 tabular-nums">-2.8</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">-1.4</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">+50%</td>
              <td className="py-3 px-3 text-right font-semibold text-slate-400 tabular-nums">-2.8</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">+50%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NonOperatingResults;
